/**
 * What it takes to define a command.
 *
 * One command = one {@link CommandDefinition}: a payload schema, a pure
 * reducer, an inverse, and a one-line human summary. The registry holds exactly
 * one per {@link CommandType}, and TypeScript checks that mapping is total, so
 * "add a command" is a mechanical four-part exercise with no way to half-finish
 * it (see the README's "How to add a command").
 */

import type { z } from 'zod';
import type { WorkspaceState } from '../document/workspace';
import type { ApplyResult } from './envelope';
import type { Command, CommandOf, CommandPayloadMap, CommandType } from './types';

/** The four things every command must supply. */
export interface CommandDefinition<K extends CommandType> {
  /** The command name; must equal the key it is registered under. */
  readonly type: K;
  /** Validates the payload. Anything that fails becomes an `invalid-payload` error. */
  readonly payload: z.ZodType<CommandPayloadMap[K]>;
  /**
   * The reducer. Pure: no clocks, no randomness, no mutation of `state`.
   * Returns a typed error rather than throwing, so a bad command in the middle
   * of a log leaves the workspace untouched.
   */
  readonly apply: (state: WorkspaceState, command: CommandOf<K>) => ApplyResult;
  /**
   * The undo command, computed against the **pre**-state.
   *
   * `null` means "not invertible" — nothing in the current set is, but the
   * signature keeps the door open for commands whose inverse genuinely does not
   * exist (a future `recipe.compactHistory`, say).
   */
  readonly invert: (state: WorkspaceState, command: CommandOf<K>) => Command | null;
  /**
   * One line of English describing the change, for the changeset diff cards
   * (PLAN §4.4) and the undo menu. Computed against the pre-state so it can say
   * "Crystal 60 → Crystal 40" rather than just the new value.
   */
  readonly summarize: (state: WorkspaceState, command: CommandOf<K>) => string;
}

/**
 * Identity helper that pins the generic parameter.
 *
 * Without it every definition would need an explicit type argument for the
 * payload/apply/invert signatures to line up.
 */
export function defineCommand<K extends CommandType>(
  definition: CommandDefinition<K>,
): CommandDefinition<K> {
  return definition;
}

/**
 * The command id an inverse carries: the source id with an `undo:` prefix.
 *
 * Deterministic (so replaying a log and inverting it twice gives the same ids)
 * and traceable (the outbox can pair an undo with what it undid). Truncated to
 * the 64-character id limit for pathologically long source ids.
 */
export function undoCommandId(sourceCommandId: string): string {
  return `undo:${sourceCommandId}`.slice(0, 64);
}

/**
 * Build an inverse command envelope.
 *
 * The `timestamp` is **not** "now" — it is the modification time the inverse
 * must restore, normally the pre-state document's `updatedAt`. The reducer
 * stamps `updatedAt` from the envelope timestamp, so passing the old value is
 * what makes `apply(invert(s, c)) ∘ apply(c) = identity` hold exactly rather
 * than "except for the timestamp". Callers must therefore not re-stamp an
 * inverse's timestamp before dispatching it.
 */
export function inverseCommand<K extends CommandType>(
  source: Command,
  type: K,
  payload: CommandPayloadMap[K],
  timestamp: number,
): CommandOf<K> {
  return {
    commandId: undoCommandId(source.commandId),
    type,
    payload,
    timestamp,
    actor: source.actor,
  };
}
